/**
 * Protected Route Component
 *
 * Wraps routes that should be protected by feature flags.
 * Redirects users to appropriate pages when features are disabled.
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useFeatureFlags } from "../contexts/FeatureFlagContext";
import { useAuthStore } from "../stores/authStore";

interface ProtectedRouteProps {
  children: React.ReactNode;
  featureFlag?: keyof import("../services/featureFlagService").FeatureFlags;
  fallbackPath?: string;
  requireAuth?: boolean;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  featureFlag,
  fallbackPath = "/",
  requireAuth = true,
  allowedRoles = [],
}) => {
  const { isEnabled } = useFeatureFlags();
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  // Feature flags hydrate with defaults immediately and refresh in the background
  // (FeatureFlagProvider). Never block the route tree on flag `loading` — that
  // caused an indefinite full-page spinner when refresh stalled (Strict Mode /
  // hung initialize). Gate below uses current defaults/cache/API flags.

  // Check authentication if required
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check user roles if specified
  if (allowedRoles.length > 0 && (!user || !allowedRoles.includes(user.role))) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check feature flag (only if specified)
  if (featureFlag && !isEnabled(featureFlag)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
